import { z } from "zod";

export const ImageGenerationInputSchema = z.object({
  prompt: z.string().min(1, "Prompt cannot be empty"),
  model: z.enum(["gemini-2.5-flash-image", "gemini-3.1-flash-image-preview", "gemini-3-pro-image-preview"]).optional().default("gemini-2.5-flash-image"),
  output_format: z.enum(["base64", "url"]).optional().default("base64"),
  negative_prompt: z.string().optional(),
  style: z.enum(["photorealistic", "artistic", "cartoon", "sketch", "digital_art"]).optional(),
  aspect_ratio: z.enum(["1:1", "16:9", "9:16", "4:3", "3:4", "2:3", "3:2", "4:5", "5:4", "21:9", "1:4", "4:1", "1:8", "8:1"]).optional().default("1:1"),
  seed: z.number().int().min(0).optional()
});

export type ImageGenerationInput = z.infer<typeof ImageGenerationInputSchema>;

export interface ImageGenerationResult {
  imageData: string;
  format: string;
  model: string;
  generationTime?: number;
  size?: string;
  filePath?: string;
  fileName?: string;
  fileUrl?: string;
  fileSize?: number;
}

export interface ImageGenerationOptions {
  prompt: string;
  model: string;
  outputFormat: string;
  negativePrompt?: string;
  style?: string;
  aspectRatio: string;
  seed?: number;
  fetchTimeout: number;
  saveToFile?: boolean;
  uploadToR2?: boolean;
  saveDirectory?: string;
  filePrefix?: string;
}

// Video Generation Schemas
export const VideoGenerationInputSchema = z.object({
  prompt: z.string().min(1, "Prompt cannot be empty"),
  model: z.enum(["veo-3.0-generate-001"]).optional().default("veo-3.0-generate-001"),
  duration: z.enum(["4s", "8s", "12s"]).optional().default("4s"),
  output_format: z.enum(["mp4", "webm"]).optional().default("mp4"),
  aspect_ratio: z.enum(["1:1", "16:9", "9:16", "4:3", "3:4", "2:3", "3:2", "4:5", "5:4", "21:9", "1:4", "4:1", "1:8", "8:1"]).optional().default("16:9"),
  fps: z.number().int().min(1).max(60).optional().default(24),
  image_input: z.string().optional().describe("Starting frame image - supports file paths, URLs, or base64 data URIs"),
  style: z.enum(["realistic", "cinematic", "artistic", "cartoon", "animation"]).optional(),
  camera_movement: z.enum(["static", "pan_left", "pan_right", "zoom_in", "zoom_out", "dolly_forward", "dolly_backward"]).optional(),
  seed: z.number().int().min(0).optional()
});

export type VideoGenerationInput = z.infer<typeof VideoGenerationInputSchema>;

export interface VideoGenerationResult {
  videoData: string;
  format: string;
  model: string;
  duration: string;
  aspectRatio: string;
  fps: number;
  generationTime?: number;
  size?: string;
  operationId?: string;
  filePath?: string;
  fileName?: string;
  fileUrl?: string;
  fileSize?: number;
}

export interface VideoGenerationOptions {
  prompt: string;
  model: string;
  duration: string;
  outputFormat: string;
  aspectRatio: string;
  fps: number;
  imageInput?: string;
  style?: string;
  cameraMovement?: string;
  seed?: number;
  fetchTimeout: number;
  saveToFile?: boolean;
  uploadToR2?: boolean;
  saveDirectory?: string;
  filePrefix?: string;
}

// Image Editing Schemas
export const ImageEditingInputSchema = z.object({
  operation: z.enum([
    "inpaint",
    "outpaint",
    "style_transfer",
    "object_manipulation",
    "multi_image_compose"
  ]).describe("Type of image editing operation to perform"),

  input_image: z.string().describe("Input image - supports file paths, URLs, or base64 data URIs (e.g., '/path/to/image.png', 'https://example.com/image.jpg', or 'data:image/png;base64,...')"),

  prompt: z.string().min(1, "Prompt cannot be empty").describe("Text description of the desired edit"),

  // Inpainting specific fields
  mask_image: z.string().optional().describe("Mask image for inpainting - supports file paths, URLs, or base64 data URIs (white = edit area, black = keep)"),
  mask_prompt: z.string().optional().describe("Text description of the area to mask for editing"),

  // Outpainting specific fields
  expand_direction: z.enum(["all", "left", "right", "top", "bottom", "horizontal", "vertical"]).optional().describe("Direction to expand the image"),
  expansion_ratio: z.number().min(0.1).max(3.0).optional().default(1.5).describe("How much to expand the image (1.0 = no expansion)"),

  // Style transfer specific fields
  style_image: z.string().optional().describe("Style reference image - supports file paths, URLs, or base64 data URIs"),
  style_strength: z.number().min(0.1).max(1.0).optional().default(0.7).describe("Strength of style application"),

  // Object manipulation specific fields
  target_object: z.string().optional().describe("Description of the object to manipulate"),
  manipulation_type: z.enum(["move", "resize", "remove", "replace", "duplicate"]).optional().describe("Type of object manipulation"),
  target_position: z.string().optional().describe("New position for the object (e.g., 'center', 'top-left')"),

  // Multi-image composition specific fields
  secondary_images: z.array(z.string()).optional().describe("Array of secondary images - each supports file paths, URLs, or base64 data URIs"),
  composition_layout: z.enum(["blend", "collage", "overlay", "side_by_side"]).optional().describe("How to combine multiple images"),
  blend_mode: z.enum(["normal", "multiply", "screen", "overlay", "soft_light"]).optional().describe("Blending mode for image composition"),

  // Common optional fields
  negative_prompt: z.string().optional().describe("What to avoid in the edited image"),
  strength: z.number().min(0.1).max(1.0).optional().default(0.8).describe("Strength of the editing effect"),
  guidance_scale: z.number().min(1.0).max(20.0).optional().default(7.5).describe("How closely to follow the prompt"),
  seed: z.number().int().min(0).optional().describe("Random seed for reproducible results"),
  output_format: z.enum(["base64", "url"]).optional().default("base64").describe("Output format for the edited image"),
  quality: z.enum(["draft", "standard", "high"]).optional().default("standard").describe("Quality level of the editing")
});

export type ImageEditingInput = z.infer<typeof ImageEditingInputSchema>;

export interface ImageEditingResult {
  editedImageData: string;
  format: string;
  operation: string;
  processingTime?: number;
  originalSize?: string;
  editedSize?: string;
  filePath?: string;
  fileName?: string;
  fileUrl?: string;
  fileSize?: number;
  metadata?: {
    prompt: string;
    operation: string;
    strength?: number;
    guidanceScale?: number;
    seed?: number;
  };
}

export interface ImageEditingOptions {
  operation: string;
  inputImage: string;
  prompt: string;
  maskImage?: string;
  maskPrompt?: string;
  expandDirection?: string;
  expansionRatio?: number;
  styleImage?: string;
  styleStrength?: number;
  targetObject?: string;
  manipulationType?: string;
  targetPosition?: string;
  secondaryImages?: string[];
  compositionLayout?: string;
  blendMode?: string;
  negativePrompt?: string;
  strength: number;
  guidanceScale: number;
  seed?: number;
  outputFormat: string;
  quality: string;
  fetchTimeout: number;
  saveToFile?: boolean;
  uploadToR2?: boolean;
  saveDirectory?: string;
  filePrefix?: string;
}

// Jimp Image Crop Schemas
export const JimpCropInputSchema = z.object({
  input_image: z.string().describe("Input image - supports file paths, URLs, or base64 data URIs"),
  mode: z.enum(["manual", "center", "top_left", "top_right", "bottom_left", "bottom_right", "aspect_ratio"]).optional().default("manual").describe("Crop mode"),
  x: z.number().int().min(0).optional().describe("X coordinate for crop start (manual mode)"),
  y: z.number().int().min(0).optional().describe("Y coordinate for crop start (manual mode)"),
  width: z.number().int().min(1).optional().describe("Width of crop region"),
  height: z.number().int().min(1).optional().describe("Height of crop region"),
  aspect_ratio: z.string().optional().describe("Aspect ratio (e.g., '16:9', '4:3')"),
  output_format: z.enum(["png", "jpeg", "bmp"]).optional().default("png").describe("Output image format"),
  quality: z.number().int().min(0).max(100).optional().describe("JPEG quality (0-100)")
});

export type JimpCropInput = z.infer<typeof JimpCropInputSchema>;

// Jimp Image Resize Schemas
export const JimpResizeInputSchema = z.object({
  input_image: z.string().describe("Input image - supports file paths, URLs, or base64 data URIs"),
  width: z.number().int().min(1).optional().describe("Target width in pixels"),
  height: z.number().int().min(1).optional().describe("Target height in pixels"),
  scale: z.number().min(0.01).max(10.0).optional().describe("Scale factor (e.g., 0.5 for 50%, 2.0 for 200%)"),
  maintain_aspect_ratio: z.boolean().optional().default(true).describe("Maintain aspect ratio when resizing"),
  algorithm: z.enum(["nearestNeighbor", "bilinear", "bicubic", "hermite", "bezier"]).optional().default("bilinear").describe("Resize algorithm"),
  output_format: z.enum(["png", "jpeg", "bmp"]).optional().default("png").describe("Output image format"),
  quality: z.number().int().min(0).max(100).optional().describe("JPEG quality (0-100)")
});

export type JimpResizeInput = z.infer<typeof JimpResizeInputSchema>;

// Jimp Image Rotate Schemas
export const JimpRotateInputSchema = z.object({
  input_image: z.string().describe("Input image - supports file paths, URLs, or base64 data URIs"),
  angle: z.number().describe("Rotation angle in degrees (positive = clockwise, negative = counter-clockwise)"),
  background_color: z.string().optional().describe("Background color for areas outside the rotated image (CSS color format, e.g., '#ffffff', 'white')"),
  output_format: z.enum(["png", "jpeg", "bmp"]).optional().default("png").describe("Output image format"),
  quality: z.number().int().min(0).max(100).optional().describe("JPEG quality (0-100)")
});

export type JimpRotateInput = z.infer<typeof JimpRotateInputSchema>;

// Jimp Image Mask Schemas
export const JimpMaskInputSchema = z.object({
  input_image: z.string().describe("Input image to apply mask to - supports file paths, URLs, or base64 data URIs"),
  mask_image: z.string().describe("Grayscale mask image (black = transparent, white = opaque) - supports file paths, URLs, or base64 data URIs"),
  output_format: z.enum(["png", "jpeg", "bmp"]).optional().default("png").describe("Output image format"),
  quality: z.number().int().min(0).max(100).optional().describe("JPEG quality (0-100)")
});

export type JimpMaskInput = z.infer<typeof JimpMaskInputSchema>;

// Background Removal Schemas
export const BackgroundRemovalInputSchema = z.object({
  input_image: z.string().describe("Input image - supports file paths, URLs, or base64 data URIs"),
  quality: z.enum(["fast", "balanced", "high"]).optional().default("balanced").describe("Processing quality (fast = quick but less accurate, high = slower but more accurate)"),
  output_format: z.enum(["png", "jpeg"]).optional().default("png").describe("Output image format (PNG preserves transparency, JPEG requires background color)"),
  background_color: z.string().optional().describe("Background color for JPEG output (CSS color format, e.g., '#ffffff', 'white')"),
  jpeg_quality: z.number().int().min(0).max(100).optional().default(85).describe("JPEG quality (0-100)")
});

export type BackgroundRemovalInput = z.infer<typeof BackgroundRemovalInputSchema>;

// Playwright Screenshot Schemas

// Full Page Screenshot Schema
export const PlaywrightFullPageScreenshotInputSchema = z.object({
  url: z.string().url().describe("URL of the webpage to capture"),
  format: z.enum(["png", "jpeg"]).optional().default("png").describe("Screenshot format"),
  quality: z.number().int().min(0).max(100).optional().describe("JPEG quality (0-100), only applicable for jpeg format"),
  timeout: z.number().int().min(1000).max(120000).optional().default(30000).describe("Navigation timeout in milliseconds"),
  wait_until: z.enum(["load", "domcontentloaded", "networkidle"]).optional().default("networkidle").describe("When to consider navigation successful"),
  viewport: z.object({
    width: z.number().int().min(320).max(3840).default(1920),
    height: z.number().int().min(240).max(2160).default(1080)
  }).optional().describe("Viewport dimensions")
});

export type PlaywrightFullPageScreenshotInput = z.infer<typeof PlaywrightFullPageScreenshotInputSchema>;

// Viewport Screenshot Schema
export const PlaywrightViewportScreenshotInputSchema = z.object({
  url: z.string().url().describe("URL of the webpage to capture"),
  format: z.enum(["png", "jpeg"]).optional().default("png").describe("Screenshot format"),
  quality: z.number().int().min(0).max(100).optional().describe("JPEG quality (0-100), only applicable for jpeg format"),
  timeout: z.number().int().min(1000).max(120000).optional().default(30000).describe("Navigation timeout in milliseconds"),
  wait_until: z.enum(["load", "domcontentloaded", "networkidle"]).optional().default("networkidle").describe("When to consider navigation successful"),
  viewport: z.object({
    width: z.number().int().min(320).max(3840).default(1920),
    height: z.number().int().min(240).max(2160).default(1080)
  }).optional().describe("Viewport dimensions")
});

export type PlaywrightViewportScreenshotInput = z.infer<typeof PlaywrightViewportScreenshotInputSchema>;

// Element Screenshot Schema
export const PlaywrightElementScreenshotInputSchema = z.object({
  url: z.string().url().describe("URL of the webpage to capture"),
  selector: z.string().min(1).describe("CSS selector, text content, or role of the element to capture"),
  selector_type: z.enum(["css", "text", "role"]).optional().default("css").describe("Type of selector (css, text, or role)"),
  format: z.enum(["png", "jpeg"]).optional().default("png").describe("Screenshot format"),
  quality: z.number().int().min(0).max(100).optional().describe("JPEG quality (0-100), only applicable for jpeg format"),
  timeout: z.number().int().min(1000).max(120000).optional().default(30000).describe("Navigation and element wait timeout in milliseconds"),
  wait_until: z.enum(["load", "domcontentloaded", "networkidle"]).optional().default("networkidle").describe("When to consider navigation successful"),
  viewport: z.object({
    width: z.number().int().min(320).max(3840).default(1920),
    height: z.number().int().min(240).max(2160).default(1080)
  }).optional().describe("Viewport dimensions"),
  wait_for_selector: z.boolean().optional().default(true).describe("Wait for the selector to be visible before capturing")
});

export type PlaywrightElementScreenshotInput = z.infer<typeof PlaywrightElementScreenshotInputSchema>;

// ---- Music Generation Schemas (Minimax Music 2.5) ----

export const MusicGenerationInputSchema = z.object({
  lyrics: z.string().min(1).max(3500)
    .describe("Song lyrics with optional structure tags like [Verse], [Chorus], [Bridge]. Use \\n for line breaks. Required, 1-3500 chars."),
  prompt: z.string().max(2000).optional()
    .describe("Music style/mood description (e.g., 'Pop, melancholic, piano-driven ballad'). Optional, 0-2000 chars."),
  model: z.enum(["music-2.5"]).optional().default("music-2.5")
    .describe("Music generation model"),
  audio_format: z.enum(["mp3", "wav"]).optional().default("mp3")
    .describe("Output audio format"),
  sample_rate: z.enum(["16000", "24000", "32000", "44100"]).optional().default("44100")
    .describe("Audio sample rate in Hz"),
  bitrate: z.enum(["32000", "64000", "128000", "256000"]).optional().default("256000")
    .describe("Audio bitrate in bps"),
});

export type MusicGenerationInput = z.infer<typeof MusicGenerationInputSchema>;

export interface MusicGenerationResult {
  audioUrl: string;
  format: string;
  model: string;
  duration: number;
  generationTime: number;
  filePath?: string;
  fileName?: string;
  fileUrl?: string;
  fileSize?: number;
}

// ---- ElevenLabs Sound Effects Schemas ----

export const SfxGenerationInputSchema = z.object({
  text: z.string().min(1).max(500)
    .describe("Text prompt describing the sound effect (e.g., 'Thunder rumbling with heavy rain')"),
  duration_seconds: z.number().min(0.5).max(30).optional()
    .describe("Duration in seconds (0.5-30). If omitted, AI decides optimal length."),
  prompt_influence: z.number().min(0).max(1).optional().default(0.3)
    .describe("How closely to follow the prompt (0=creative, 1=literal). Default: 0.3"),
  loop: z.boolean().optional().default(false)
    .describe("Create seamless looping audio (great for ambient sounds)"),
});

export type SfxGenerationInput = z.infer<typeof SfxGenerationInputSchema>;

// ---- ElevenLabs Music Generation Schemas ----

export const ElevenLabsMusicGenerationInputSchema = z.object({
  prompt: z.string().min(1).max(2000)
    .describe("Text description of the music to generate (e.g., 'An upbeat electronic track with synth pads and driving bass')"),
  music_length_ms: z.number().int().min(3000).max(600000).optional().default(30000)
    .describe("Duration in milliseconds (3000-600000, i.e., 3s to 10min). Default: 30000 (30s)"),
  force_instrumental: z.boolean().optional().default(false)
    .describe("Remove vocals and generate instrumental only"),
});

export type ElevenLabsMusicGenerationInput = z.infer<typeof ElevenLabsMusicGenerationInputSchema>;